import { z } from 'zod';
import {
  SelectableTicketCategorySchema,
  TicketCategorySchema,
  TicketStatusSchema,
} from '../enums/ticket.js';
import { IsoDateStringSchema, UuidV7Schema } from './common.js';

/**
 * Contrats des tickets lead — question d'un vendeur sur SA fiche +
 * réponses admin. Un ticket est TOUJOURS rattaché à un lead ; le corps
 * des messages est du texte libre borné à 600 chars (enforce ici, pas
 * en DB).
 */

/** Longueur max du texte libre d'un message de ticket. */
export const TICKET_BODY_MAX_LENGTH = 600;

/**
 * Longueur MIN du détail à la création d'un ticket (après trim). Décision
 * Cyrille 09/2026 : un motif seul ne suffit pas, quelques mots de détail sont
 * obligatoires — sinon l'équipe rappelle pour comprendre la demande. 10
 * caractères = le plancher d'une phrase courte (« Client absent », « Faux n° »).
 * Ne s'applique qu'à la création : une réponse admin reste libre.
 */
export const TICKET_BODY_MIN_LENGTH = 10;

/**
 * Message d'un ticket (question initiale du vendeur, réponses admin).
 * L'API embarque en plus un résumé `author` (id, matricule, role,
 * firstName, lastName…) dont la shape suit les users embarqués des
 * endpoints leads — l'AnonymizeInterceptor s'applique dessus. On ne le
 * fige pas ici (contrat minimal).
 */
export const TicketMessageSchema = z.object({
  id: UuidV7Schema,
  ticketId: UuidV7Schema,
  authorUserId: UuidV7Schema,
  body: z.string(),
  createdAt: IsoDateStringSchema,
});
export type TicketMessage = z.infer<typeof TicketMessageSchema>;

export const TicketSchema = z.object({
  id: UuidV7Schema,
  leadId: UuidV7Schema,
  createdByUserId: UuidV7Schema,
  /** Snapshot du partner du lead au moment de la création (scoping B2B). */
  partnerId: UuidV7Schema,
  category: TicketCategorySchema,
  status: TicketStatusSchema,
  createdAt: IsoDateStringSchema,
  updatedAt: IsoDateStringSchema,
  /** Posé à la clôture, effacé à la réouverture. */
  resolvedAt: IsoDateStringSchema.nullable(),
  /** Présent sur les endpoints détail / par-lead ; absent des listings légers. */
  messages: z.array(TicketMessageSchema).optional(),
});
export type Ticket = z.infer<typeof TicketSchema>;

/**
 * Payload du POST /leads/:leadId/tickets — crée le ticket + son 1er message.
 * Seuls les motifs PROPOSÉS sont acceptés (pas les valeurs héritées) ; le
 * détail est trimé puis borné [MIN, MAX] — la garde vaut côté API, l'UI ne
 * fait que l'annoncer.
 */
export const CreateTicketSchema = z.object({
  category: SelectableTicketCategorySchema,
  body: z
    .string()
    .trim()
    .min(TICKET_BODY_MIN_LENGTH, {
      message: `Précisez votre demande en quelques mots (${TICKET_BODY_MIN_LENGTH} caractères minimum).`,
    })
    .max(TICKET_BODY_MAX_LENGTH),
});
export type CreateTicketInput = z.infer<typeof CreateTicketSchema>;

/** Payload du POST /tickets/:id/messages (réponse admin). */
export const ReplyTicketSchema = z.object({
  body: z.string().min(1).max(TICKET_BODY_MAX_LENGTH),
});
export type ReplyTicketInput = z.infer<typeof ReplyTicketSchema>;

/** Payload du PATCH /tickets/:id/status. */
export const UpdateTicketStatusSchema = z.object({
  status: TicketStatusSchema,
});
export type UpdateTicketStatusInput = z.infer<typeof UpdateTicketStatusSchema>;

/** Query params du GET /tickets (inbox admin). */
export const ListTicketsQuerySchema = z.object({
  status: TicketStatusSchema.optional(),
});
export type ListTicketsQuery = z.infer<typeof ListTicketsQuerySchema>;
